import { BoxV2 as Box, Flex, RoleGuard } from 'portal-commons';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { faSquarePollHorizontal } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid } from '@material-ui/core';

import { useControllers } from './hooks';
import { CampaignAttributesSectionFormValues } from './types';
import CampaignAttributeNew from '../CampaignAttributeNew';
import EmbeddedLinkPopup from '../EmbeddedLinkPopup';
import MessageFlowAttributePopup from '../MessageFlowAttributePopup';
import { getCampaignTermsAndConditions } from '../../apiServices';
import { CampaignAttributeKey } from '../../types';
import { CampaignDetail } from '@/apis/core';

interface CampaignAttributesSectionProps {
  readonly?: boolean;
  campaign: CampaignDetail;
  disabledFields?: string[];
  onAttributeChange?: (
    value: boolean,
    key: CampaignAttributeKey,
    label: string
  ) => void;
  onDataChange?: (value: Record<string, unknown>) => void;
}

enum PopupType {
  None = 'none',
  SubscriberOptin = 'subscriberOptin',
  SubscriberOptout = 'subscriberOptout',
  SubscriberHelp = 'subscriberHelp',
  EmbeddedLink = 'embeddedLink',
}

const CampaignAttributesSection: FunctionComponent<
  CampaignAttributesSectionProps
> = ({
  readonly,
  campaign,
  disabledFields = [],
  onAttributeChange,
  onDataChange,
}) => {
  const {
    control,
    formState: { dirtyFields },
    trigger,
    reset,
  } = useForm<CampaignAttributesSectionFormValues>({
    mode: 'onTouched',
    defaultValues: {
      optinKeywords: campaign.optinKeywords?.split(',') || [],
      optinMessage: campaign.optinMessage || '',
      optoutKeywords: campaign.optoutKeywords?.split(',') || [],
      optoutMessage: campaign.optoutMessage || '',
      helpKeywords: campaign.helpKeywords?.split(',') || [],
      helpMessage: campaign.helpMessage || '',
      embeddedLinkSample: campaign.embeddedLinkSample || '',
    },
  });
  const {
    optinKeywords,
    setOptinKeywords,
    optinMessage,
    setOptinMessage,
    validateOptinMessage,
    optinMessageError,
    optoutKeywords,
    setOptoutKeywords,
    optoutMessage,
    setOptoutMessage,
    validateOptoutMessage,
    optoutMessageError,
    helpKeywords,
    setHelpKeywords,
    helpMessage,
    setHelpMessage,
    validateHelpMessage,
    helpMessageError,
    embeddedLinkSample,
    embeddedLinkSampleError,
    setEmbeddedLinkSample,
    validateEmbeddedLinkSample,
  } = useControllers(
    control,
    campaign.subscriberOptin,
    campaign.subscriberOptout,
    campaign.subscriberHelp,
    campaign.embeddedLink
  );
  const [termsAndConditions, setTermsAndConditions] = useState<string[]>([]);
  const [popupType, setPopupType] = useState(PopupType.None);

  const handleAdditionalSetupToggle = (type: PopupType, active: boolean) => {
    if (!active && popupType !== type) {
      return;
    }
    setPopupType(active ? type : PopupType.None);
  };

  const handleOptinPopupClose = async () => {
    if (readonly) {
      setPopupType(PopupType.None);
    }
    if (dirtyFields.optinKeywords || dirtyFields.optinMessage) {
      const response = await trigger('optinMessage');
      if (response) {
        onDataChange?.({
          optinKeywords: optinKeywords.join(','),
          optinMessage,
        });
        setPopupType(PopupType.None);
      }
    }
  };

  const handleOptoutPopupClose = async () => {
    if (readonly) {
      setPopupType(PopupType.None);
    }
    if (dirtyFields.optoutKeywords || dirtyFields.optoutMessage) {
      const response = await trigger('optoutMessage');
      if (response) {
        onDataChange?.({
          optoutKeywords: optoutKeywords.join(','),
          optoutMessage,
        });
        setPopupType(PopupType.None);
      }
    }
  };

  const handleHelpPopupClose = async () => {
    if (readonly) {
      setPopupType(PopupType.None);
    }
    if (dirtyFields.helpKeywords || dirtyFields.helpMessage) {
      const response = await trigger('helpMessage');
      if (response) {
        onDataChange?.({
          helpKeywords: helpKeywords.join(','),
          helpMessage,
        });
        setPopupType(PopupType.None);
      }
    }
  };

  const handleEmbeddedLinkPopupClose = async () => {
    if (readonly) {
      setPopupType(PopupType.None);
    }
    if (dirtyFields.embeddedLinkSample) {
      const response = await trigger('embeddedLinkSample');
      if (response) {
        onDataChange?.({
          embeddedLinkSample,
        });
        setPopupType(PopupType.None);
      }
    }
  };

  useEffect(() => {
    (async () => {
      const response = await getCampaignTermsAndConditions();
      setTermsAndConditions(response);
    })();
  }, []);

  useEffect(() => {
    reset({
      optinKeywords: campaign.optinKeywords?.split(',') || [],
      optinMessage: campaign.optinMessage || '',
      optoutKeywords: campaign.optoutKeywords?.split(',') || [],
      optoutMessage: campaign.optoutMessage || '',
      helpKeywords: campaign.helpKeywords?.split(',') || [],
      helpMessage: campaign.helpMessage || '',
      embeddedLinkSample: campaign.embeddedLinkSample || '',
    });
  }, [campaign, reset]);

  return (
    <Grid item xs={12}>
      <div
        className="attributes details"
        data-testid="campaignDetailCampaignAndContentAttributes"
      >
        <div className="details-heading">
          <Flex sx={{ alignItems: 'center' }}>
            <Box sx={{ mr: 'xs' }}>
              <FontAwesomeIcon
                icon={faSquarePollHorizontal}
                size="xl"
                color="#49535D"
              />
            </Box>
            <h3 className="heading1">Campaign Attributes</h3>
          </Flex>
        </div>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 'l',
            rowGap: 's',
          }}
        >
          <RoleGuard
            feature="campaignDetail.editCampaignAttributes"
            render={(authorized) => (
              <CampaignAttributeNew
                type="view"
                id="subscriberOptIn"
                disabled={disabledFields.includes('subscriberOptin')}
                value={campaign.subscriberOptin}
                label="Subscriber Opt-in"
                tooltip="Indicates whether the campaign is collecting and processing consumer opt-ins"
                additionalSetup={
                  <MessageFlowAttributePopup
                    disabled={readonly}
                    defaultReadonly
                    showCharCounter
                    editable={authorized}
                    hideCloseButton
                    label="Opt-in"
                    keywordsTooltip="A keyword consumers can text to opt-in."
                    messageTooltip="If consumers can text in a keyword, the response should include the Brand DBA name, confirmation of opt-in enrollment to a recurring message campaign, how to get help, and clear description of how to opt-out."
                    keywords={optinKeywords}
                    message={optinMessage}
                    error={optinMessageError?.message}
                    onKeywordsChange={setOptinKeywords}
                    onMessageChange={setOptinMessage}
                    onMessageBlur={validateOptinMessage}
                    onClose={handleOptinPopupClose}
                  />
                }
                additionalSetupActive={popupType === PopupType.SubscriberOptin}
                setAdditionalSetupActive={(active) =>
                  handleAdditionalSetupToggle(PopupType.SubscriberOptin, active)
                }
              />
            )}
          />
          <RoleGuard
            feature="campaignDetail.editCampaignAttributes"
            render={(authorized) => (
              <CampaignAttributeNew
                type={readonly || !authorized ? 'view' : ''}
                campaignInfo={campaign}
                value={campaign.embeddedLink}
                id="embeddedLink"
                disabled={disabledFields.includes('embeddedLink')}
                label="Embedded Link"
                tooltip="Indicates whether the campaign is using an embedded link of any kind. Note that public URL shorteners (bitly, tinyurl) are not accepted"
                handleChange={onAttributeChange}
                additionalSetup={
                  <EmbeddedLinkPopup
                    disabled={readonly}
                    editable={authorized}
                    defaultReadonly
                    hideCloseButton
                    error={embeddedLinkSampleError?.message}
                    value={embeddedLinkSample}
                    onChange={setEmbeddedLinkSample}
                    onBlur={validateEmbeddedLinkSample}
                    onClose={handleEmbeddedLinkPopupClose}
                  />
                }
                additionalSetupActive={popupType === PopupType.EmbeddedLink}
                setAdditionalSetupActive={(active) =>
                  handleAdditionalSetupToggle(PopupType.EmbeddedLink, active)
                }
              />
            )}
          />
          <RoleGuard
            feature="campaignDetail.editCampaignAttributes"
            render={(authorized) => (
              <CampaignAttributeNew
                type="view"
                value={campaign.subscriberOptout}
                label="Subscriber Opt-Out"
                id="subscriberOptOut"
                disabled={disabledFields.includes('subscriberOptout')}
                tooltip="Indicates whether the campaign is collecting and processing consumer opt-outs"
                additionalSetup={
                  <MessageFlowAttributePopup
                    disabled={readonly}
                    defaultReadonly
                    messageRequired
                    showCharCounter
                    editable={authorized}
                    hideCloseButton
                    label="Opt-out"
                    keywordsTooltip="A keyword consumers can text to opt-out."
                    messageTooltip="The response to the STOP keyword may include the Brand DBA name but should include an acknowledgement of opt-out request and confirmation that no further messages will be sent."
                    keywords={optoutKeywords}
                    message={optoutMessage}
                    error={optoutMessageError?.message}
                    onKeywordsChange={setOptoutKeywords}
                    onMessageChange={setOptoutMessage}
                    onMessageBlur={validateOptoutMessage}
                    onClose={handleOptoutPopupClose}
                  />
                }
                additionalSetupActive={popupType === PopupType.SubscriberOptout}
                setAdditionalSetupActive={(active) =>
                  handleAdditionalSetupToggle(
                    PopupType.SubscriberOptout,
                    active
                  )
                }
              />
            )}
          />
          <RoleGuard
            feature="campaignDetail.editCampaignAttributes"
            render={(authorized) => (
              <CampaignAttributeNew
                type={readonly || !authorized ? 'view' : ''}
                campaignInfo={campaign}
                value={campaign.embeddedPhone}
                id="embeddedPhone"
                disabled={disabledFields.includes('embeddedPhone')}
                label="Embedded Phone Number"
                tooltip="Indicates whether the campaign is using an embedded phone number (except the required HELP information contact phone number)"
                handleChange={onAttributeChange}
              />
            )}
          />
          <RoleGuard
            feature="campaignDetail.editCampaignAttributes"
            render={(authorized) => (
              <CampaignAttributeNew
                type="view"
                value={campaign.subscriberHelp}
                label="Subscriber Help"
                id="subscriberHelp"
                disabled={disabledFields.includes('subscriberHelp')}
                tooltip="Indicates whether the campaign has implemented message reply providing customers on how they can contact the message sender after they reply with the “HELP” keyword."
                additionalSetup={
                  <MessageFlowAttributePopup
                    disabled={readonly}
                    defaultReadonly
                    messageRequired
                    showCharCounter
                    editable={authorized}
                    hideCloseButton
                    label="Help"
                    keywordsTooltip="A keyword consumers use to get help or more information."
                    messageTooltip="The response to HELP keyword may include the Brand DBA name and additional support contact information."
                    keywords={helpKeywords}
                    message={helpMessage}
                    error={helpMessageError?.message}
                    onKeywordsChange={setHelpKeywords}
                    onMessageChange={setHelpMessage}
                    onMessageBlur={validateHelpMessage}
                    onClose={handleHelpPopupClose}
                  />
                }
                additionalSetupActive={popupType === PopupType.SubscriberHelp}
                setAdditionalSetupActive={(active) =>
                  handleAdditionalSetupToggle(PopupType.SubscriberHelp, active)
                }
              />
            )}
          />
          <RoleGuard
            feature="campaignDetail.editCampaignAttributes"
            render={(authorized) => (
              <CampaignAttributeNew
                type={readonly || !authorized ? 'view' : ''}
                campaignInfo={campaign}
                value={campaign.ageGated}
                id="ageGated"
                disabled={disabledFields.includes('ageGated')}
                label="Age-Gated Content"
                tooltip="Indicates whether the campaign include any age-gated content as defined by Carrier and CTIA guidelines"
                handleChange={onAttributeChange}
              />
            )}
          />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 'l',
            mt: 's',
          }}
        >
          <Flex sx={{ flexDirection: 'column', gap: 's' }}>
            <RoleGuard
              feature="campaignDetail.editCampaignAttributes"
              render={(authorized) => (
                <CampaignAttributeNew
                  type={readonly || !authorized ? 'view' : ''}
                  campaignInfo={campaign}
                  value={campaign.numberPool}
                  id="numberPool"
                  disabled={disabledFields.includes('numberPool')}
                  label="Number Pooling"
                  tooltip="Indicates whether more than 50 numbers will be used for the campaign."
                  handleChange={onAttributeChange}
                />
              )}
            />
            <RoleGuard
              feature="campaignDetail.editCampaignAttributes"
              render={(authorized) => (
                <CampaignAttributeNew
                  type={readonly || !authorized ? 'view' : ''}
                  campaignInfo={campaign}
                  value={campaign.directLending}
                  id="directLending"
                  disabled={disabledFields.includes('directLending')}
                  label="Direct Lending or Loan Arrangement"
                  tooltip="Indicates whether the campaign include content related to direct lending or other loan arrangements"
                  handleChange={onAttributeChange}
                />
              )}
            />
          </Flex>
          <CampaignAttributeNew
            type="view"
            label="Terms & Conditions"
            id="termsAndConditions"
            disabled={disabledFields.includes('termsAndConditions')}
            tooltip="Indicates the campaign follows CTIA messaging principles and best practices"
            notes={termsAndConditions}
            value={campaign.termsAndConditions}
          />
        </Box>
      </div>
    </Grid>
  );
};

export default CampaignAttributesSection;
